<template>
  <div class="space-y-2">
    <!-- Entity search input -->
    <div class="relative">
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        :placeholder="
          selectedEntity
            ? ''
            : tt(
                'Search HA entity (e.g. sensor.power, switch.plug)',
                'Iskanje entitete HA (npr. sensor.power, switch.plug)',
              )
        "
        class="w-full px-2.5 py-1.5 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400"
        @input="onSearchInput"
        @focus="onSearchFocus"
        @blur="onSearchBlur"
        @keydown.enter.prevent="selectFirstMatch"
        @keydown.escape="closeDropdown"
        @keydown.arrow-down.prevent="navigateDown"
        @keydown.arrow-up.prevent="navigateUp" />
      <span
        v-if="loading"
        class="absolute right-2 top-1/2 -translate-y-1/2 material-symbols-outlined text-sm text-gray-400 animate-spin">
        progress_activity
      </span>
      <button
        v-else-if="selectedEntity"
        class="absolute right-2 top-1/2 -translate-y-1/2 material-symbols-outlined text-sm text-gray-400 hover:text-gray-600"
        @mousedown.prevent="clearSelection"
        :title="tt('Clear selection', 'Počisti izbiro')">
        close
      </button>
      <span v-else class="absolute right-2 top-1/2 -translate-y-1/2 material-symbols-outlined text-sm text-gray-400">
        search
      </span>
    </div>

    <!-- Selected entity badge -->
    <div
      v-if="selectedEntity && !showDropdown"
      class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700">
      <span class="material-symbols-outlined text-sm text-orange-500">sensors</span>
      <div class="flex-1 min-w-0">
        <div class="text-xs font-medium text-gray-900 dark:text-white truncate">
          {{ selectedEntity.friendlyName || selectedEntity.entityId }}
        </div>
        <div class="text-xs text-gray-500 dark:text-gray-400 font-mono">
          {{ selectedEntity.entityId }}
        </div>
      </div>
      <span
        class="px-1.5 py-0.5 text-xs rounded bg-orange-100 dark:bg-orange-800/40 text-orange-700 dark:text-orange-300 font-mono">
        {{ selectedEntity.domain }}
      </span>
    </div>

    <!-- Dropdown list -->
    <div
      v-if="showDropdown"
      class="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl">
      <!-- Domain filter chips -->
      <div
        v-if="domains.length > 1"
        class="flex items-center gap-1 px-2 py-1.5 border-b border-gray-200 dark:border-gray-700 flex-wrap">
        <button
          v-for="domain in ['all', ...domains]"
          :key="domain"
          :class="[
            'px-2 py-0.5 text-xs rounded-full border transition-colors',
            selectedDomain === domain
              ? 'bg-orange-500 text-white border-orange-500'
              : 'bg-transparent text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-orange-400',
          ]"
          @mousedown.prevent="
            selectedDomain = domain;
            highlightedIndex = 0;
          ">
          {{ domain === 'all' ? tt('All', 'Vse') : domain }}
        </button>
      </div>
      <ul class="py-1">
        <li
          v-for="(entity, index) in filteredEntities"
          :key="entity.id"
          :class="[
            'px-3 py-2 cursor-pointer transition-colors',
            highlightedIndex === index
              ? 'bg-orange-50 dark:bg-orange-900/30'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700',
          ]"
          @mousedown.prevent="selectEntity(entity)"
          @mouseenter="highlightedIndex = index">
          <div class="flex items-center justify-between gap-2">
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-900 dark:text-white truncate">
                {{ entity.friendlyName || entity.entityId }}
              </div>
              <div class="flex items-center gap-2 mt-0.5">
                <span class="text-xs text-gray-500 dark:text-gray-400 font-mono">{{ entity.entityId }}</span>
                <span v-if="entity.haAreaName" class="text-xs text-gray-400 dark:text-gray-500"
                  >· {{ entity.haAreaName }}</span
                >
              </div>
            </div>
            <div class="flex items-center gap-1.5 flex-shrink-0">
              <span
                class="px-1.5 py-0.5 text-xs rounded bg-orange-100 dark:bg-orange-800/40 text-orange-700 dark:text-orange-300 font-mono">
                {{ entity.domain }}
              </span>
              <span
                v-if="entity.state !== undefined"
                class="px-1.5 py-0.5 text-xs rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                {{ truncate(String(entity.state), 12) }}
              </span>
            </div>
          </div>
        </li>
      </ul>
      <div
        v-if="filteredEntities.length === 0 && !loading"
        class="px-3 py-3 text-xs text-gray-500 dark:text-gray-400 text-center">
        <span v-if="error" class="text-red-500">{{ error }}</span>
        <span v-else-if="searchQuery"
          >{{ tt('No entities matching', 'Ni entitet, ki bi se ujemale z') }} "{{ searchQuery }}"</span
        >
        <span v-else>{{
          tt('No HA entities found for this integration', 'Za to integracijo ni najdenih entitet HA')
        }}</span>
      </div>
    </div>

    <!-- Attribute / field picker (shown once entity is selected) -->
    <div v-if="selectedEntity && referenceType === 'status'" class="space-y-1">
      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400">{{
        tt('Source field', 'Polje vira')
      }}</label>
      <div class="flex gap-2">
        <select
          v-model="localValue.haField"
          class="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          @change="emitValue">
          <option value="state">{{ tt('state (main entity state)', 'state (glavno stanje entitete)') }}</option>
          <option v-for="attr in entityAttributes" :key="attr" :value="attr">
            {{ attr }}
          </option>
        </select>
        <input
          v-if="showCustomField"
          v-model="localValue.haField"
          type="text"
          placeholder="custom.attribute"
          class="w-36 px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
          @change="emitValue" />
        <button
          class="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-lg"
          @click="showCustomField = !showCustomField"
          :title="tt('Type custom attribute path', 'Vnesite pot atributa po meri')">
          <span class="material-symbols-outlined text-sm">edit</span>
        </button>
      </div>
    </div>

    <!-- Control config for HA (show service info) -->
    <div v-if="selectedEntity && referenceType === 'control'" class="space-y-2">
      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400">{{
        tt('Control service', 'Krmilna storitev')
      }}</label>
      <div class="grid grid-cols-2 gap-2">
        <div>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">{{
            tt('Service (e.g. turn_on)', 'Storitev (npr. turn_on)')
          }}</label>
          <input
            v-model="localValue.haService"
            type="text"
            placeholder="turn_on"
            class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
            @change="emitValue" />
        </div>
        <div>
          <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">{{
            tt('Service param (optional)', 'Parameter storitve (neobvezno)')
          }}</label>
          <input
            v-model="localValue.haServiceParam"
            type="text"
            placeholder="e.g. brightness_pct"
            class="w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
            @change="emitValue" />
        </div>
      </div>
      <p class="text-xs text-gray-400 dark:text-gray-500">
        {{ tt('Domain:', 'Domena:') }} <span class="font-mono">{{ selectedEntity.domain }}</span> —
        {{ brand.agentName }} {{ tt('will call', 'bo poklical') }}
        <span class="font-mono">{{ selectedEntity.domain }}.{{ localValue.haService || 'turn_on' }}</span>
        {{ tt('with entity_id', 'z entity_id') }} <span class="font-mono">{{ selectedEntity.entityId }}</span>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { apiFetch } from '../services/api';
import { brand } from '../config/brand';
import { tt } from '../utils/consoleText';

export interface HAEntityRef {
  haEntityId: string; // UUID from ha_entities table
  entityId: string; // e.g. "sensor.power_plug"
  domain: string;
  friendlyName?: string;
  // Status fields
  haField?: string; // e.g. "state", "current_power"
  haStateTrue?: string; // for boolean: HA state value meaning true
  haStateFalse?: string; // for boolean: HA state value meaning false
  scaleFactor?: number;
  valueOffset?: number;
  // Control fields
  haDomain?: string;
  haService?: string;
  haServiceOn?: string;
  haServiceOff?: string;
  haServiceParam?: string;
}

interface HAEntityRecord {
  id: string;
  entityId: string;
  domain: string;
  friendlyName?: string;
  state?: string;
  attributes?: Record<string, unknown>;
  haAreaId?: string;
  haAreaName?: string;
}

interface Props {
  modelValue?: HAEntityRef;
  integrationId: string;
  referenceType: 'status' | 'control';
  valueType?: string; // number, boolean, string, enum
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: HAEntityRef | undefined];
}>();

const searchInput = ref<HTMLInputElement>();
const searchQuery = ref('');
const showDropdown = ref(false);
const loading = ref(false);
const error = ref<string | null>(null);
const entities = ref<HAEntityRecord[]>([]);
const selectedEntity = ref<HAEntityRecord | undefined>(undefined);
const highlightedIndex = ref(0);
const selectedDomain = ref('all');
const showCustomField = ref(false);

const localValue = ref<Partial<HAEntityRef>>({
  haField: 'state',
  haDomain: '',
  haService: 'turn_on',
  haServiceOn: '',
  haServiceOff: '',
  haServiceParam: '',
  haStateTrue: 'on',
  haStateFalse: 'off',
  scaleFactor: undefined,
  valueOffset: undefined,
});

// Derive domains from loaded entities
const domains = computed(() => {
  const d = new Set(entities.value.map((e) => e.domain));
  return Array.from(d).sort();
});

// Attributes from selected entity for field picker
const entityAttributes = computed((): string[] => {
  if (!selectedEntity.value?.attributes) return [];
  const skip = new Set([
    'friendly_name',
    'supported_features',
    'supported_color_modes',
    'color_mode',
    'effect_list',
    'hs_color',
    'xy_color',
  ]);
  return Object.keys(selectedEntity.value.attributes)
    .filter((k) => !skip.has(k))
    .sort();
});

const filteredEntities = computed(() => {
  let result = entities.value;
  if (selectedDomain.value !== 'all') {
    result = result.filter((e) => e.domain === selectedDomain.value);
  }
  const q = searchQuery.value.trim().toLowerCase();
  if (q) {
    result = result.filter(
      (e) =>
        e.entityId.toLowerCase().includes(q) ||
        (e.friendlyName || '').toLowerCase().includes(q) ||
        (e.haAreaName || '').toLowerCase().includes(q) ||
        e.domain.toLowerCase().includes(q),
    );
  }
  return result.slice(0, 60);
});

function truncate(str: string, maxLen: number): string {
  return str.length > maxLen ? str.slice(0, maxLen) + '…' : str;
}

async function loadEntities() {
  if (!props.integrationId || entities.value.length > 0) return;
  loading.value = true;
  error.value = null;
  try {
    const data = await apiFetch(`/integrations/${props.integrationId}/datapoints`);
    entities.value = Array.isArray(data?.homeassistant) ? data.homeassistant : [];
  } catch (__caught: unknown) {
    const e = __caught as Error;
    error.value = e.message || tt('Failed to load HA entities', 'Entitet HA ni bilo mogoče naložiti');
    console.error('[HAEntityReferenceInput] Load error:', e);
  } finally {
    loading.value = false;
  }
}

function onSearchInput() {
  showDropdown.value = true;
  highlightedIndex.value = 0;
}

async function onSearchFocus() {
  await loadEntities();
  showDropdown.value = true;
}

function onSearchBlur() {
  setTimeout(() => {
    showDropdown.value = false;
  }, 200);
}

function closeDropdown() {
  showDropdown.value = false;
  searchInput.value?.blur();
}

function navigateDown() {
  if (highlightedIndex.value < filteredEntities.value.length - 1) highlightedIndex.value++;
}

function navigateUp() {
  if (highlightedIndex.value > 0) highlightedIndex.value--;
}

function selectFirstMatch() {
  if (filteredEntities.value.length > 0) {
    selectEntity(filteredEntities.value[highlightedIndex.value]);
  }
}

function selectEntity(entity: HAEntityRecord) {
  selectedEntity.value = entity;
  searchQuery.value = entity.friendlyName ? `${entity.friendlyName} (${entity.entityId})` : entity.entityId;
  showDropdown.value = false;
  // Set defaults from entity domain
  localValue.value.haDomain = entity.domain;
  if (props.referenceType === 'control') {
    if (['switch', 'light', 'input_boolean'].includes(entity.domain)) {
      localValue.value.haService = '';
      localValue.value.haServiceOn = 'turn_on';
      localValue.value.haServiceOff = 'turn_off';
    } else {
      localValue.value.haService = 'turn_on';
      localValue.value.haServiceOn = '';
      localValue.value.haServiceOff = '';
    }
  }
  emitValue();
}

function clearSelection() {
  selectedEntity.value = undefined;
  searchQuery.value = '';
  localValue.value = { haField: 'state', haService: 'turn_on' };
  emit('update:modelValue', undefined);
}

function emitValue() {
  if (!selectedEntity.value) {
    emit('update:modelValue', undefined);
    return;
  }
  const ref: HAEntityRef = {
    haEntityId: selectedEntity.value.id,
    entityId: selectedEntity.value.entityId,
    domain: selectedEntity.value.domain,
    friendlyName: selectedEntity.value.friendlyName,
  };
  if (props.referenceType === 'status') {
    ref.haField = localValue.value.haField || 'state';
    if (props.valueType === 'boolean') {
      ref.haStateTrue = localValue.value.haStateTrue || 'on';
      ref.haStateFalse = localValue.value.haStateFalse || 'off';
    }
    if (localValue.value.scaleFactor !== undefined) ref.scaleFactor = localValue.value.scaleFactor;
    if (localValue.value.valueOffset !== undefined) ref.valueOffset = localValue.value.valueOffset;
  } else {
    ref.haDomain = selectedEntity.value.domain;
    ref.haService = localValue.value.haService || undefined;
    ref.haServiceOn = localValue.value.haServiceOn || undefined;
    ref.haServiceOff = localValue.value.haServiceOff || undefined;
    ref.haServiceParam = localValue.value.haServiceParam || undefined;
  }
  emit('update:modelValue', ref);
}

// Initialize from modelValue
watch(
  () => props.modelValue,
  async (val) => {
    if (!val) {
      clearSelection();
      return;
    }
    await loadEntities();
    const found = entities.value.find((e) => e.id === val.haEntityId || e.entityId === val.entityId);
    if (found) {
      selectedEntity.value = found;
      searchQuery.value = found.friendlyName ? `${found.friendlyName} (${found.entityId})` : found.entityId;
    }
    Object.assign(localValue.value, val);
  },
  { immediate: true },
);

onMounted(() => {
  if (props.integrationId) loadEntities();
});
</script>
